/**
 * Router — todas as 15 rotas da Lista Canônica 4.1 (requirements.md §4.1).
 *
 * Estratégia (DD-005, RNF 3):
 * - Shell + providers = bundle principal (nunca lazy).
 * - Telas pesadas (Pipeline, Reports, Platform, Import) = lazy obrigatório.
 * - Telas leves (Dashboard, Auth, settings simples) = podem ser lazy, mas
 *   Dashboard é o LCP — fica no bundle principal para LCP rápido.
 * - `React.lazy` + `Suspense` para code-splitting por rota.
 *
 * Guards:
 * - `RequireAuth` — qualquer tela interna.
 * - `RequireRole` — telas restritas por papel (admin, platform, audit, digest, import, partners, reports).
 *
 * Rota corrente persistida em `azim.route` pelo RequireAuth para restauração
 * após reload ou reautenticação (Req 4.5, azim.route via safeStorage).
 */

import { lazy, Suspense, type ComponentType } from "react";
import { recoverableImport } from "./chunkRecovery.js";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./AuthProvider.js";
import { Shell } from "./Shell.js";
import { RequireAuth } from "./guards/RequireAuth.js";
import { RequireRole } from "./guards/RequireRole.js";
import { AuthPage } from "../pages/auth/AuthPage.js";
import { DashboardPage } from "../pages/dashboard/DashboardPage.js";
import type { RbacContext } from "../shared/rbac/rbac.js";
import type { UserRole } from "../types/domain.js";

// ---------------------------------------------------------------------------
// Code-splitting: telas pesadas (DD-005, RNF 3)
// ---------------------------------------------------------------------------

/** Placeholder de carregamento enquanto o chunk lazy é baixado. */
function PageLoader() {
  return (
    <div
      role="status"
      aria-label="Carregando página"
      style={{ padding: "2rem", textAlign: "center", color: "var(--fg-3)" }}
    >
      Carregando…
    </div>
  );
}

// Lazy imports — cada um gera um chunk separado no build (DD-005)
//
// `lazyNamed` embrulha o import dinâmico em `recoverableImport`: quando o chunk sumiu do
// servidor porque um deploy aconteceu com a aba aberta, a página se recarrega UMA vez em vez
// de mostrar "Algo deu errado" — que rotularia um deploy como defeito de aplicação. A defesa
// principal contra essa janela é a afinidade de sessão do BackendConfig (LDG-0126); isto é a
// rede para quando o pod ao qual o cookie prendia o cliente é removido no fim do rollout.
function lazyNamed<K extends string>(
  load: () => Promise<Record<K, ComponentType<Record<string, never>>>>,
  exportName: K,
) {
  return lazy(async () => {
    const modulo = await recoverableImport(load, {
      reload: () => { window.location.reload(); },
    });
    return { default: modulo[exportName] };
  });
}
const PipelinePage = lazyNamed(() => import("../pages/pipeline/PipelinePage.js"), "PipelinePage");
const ReportsPage = lazyNamed(() => import("../pages/reports/ReportsPage.js"), "ReportsPage");
const PlatformPage = lazyNamed(() => import("../pages/platform/PlatformPage.js"), "PlatformPage");
const ImportPage = lazyNamed(() => import("../pages/import/ImportPage.js"), "ImportPage");
const AccountsPage = lazyNamed(() => import("../pages/accounts/AccountsPage.js"), "AccountsPage");
const Account360Page = lazyNamed(() => import("../pages/accounts/Account360Page.js"), "Account360Page");
const ActivitiesPage = lazyNamed(() => import("../pages/activities/ActivitiesPage.js"), "ActivitiesPage");
const PartnersPage = lazyNamed(() => import("../pages/partners/PartnersPage.js"), "PartnersPage");
const PartnerDetailPage = lazyNamed(() => import("../pages/partners/PartnerDetailPage.js"), "PartnerDetailPage");
const InboxPage = lazyNamed(() => import("../pages/inbox/InboxPage.js"), "InboxPage");
const ConversationsPage = lazyNamed(() => import("../pages/conversations/ConversationsPage.js"), "ConversationsPage");
const OpportunityDetailPage = lazyNamed(() => import("../pages/opportunity/OpportunityDetailPage.js"), "OpportunityDetailPage");
const ChannelsPage = lazyNamed(() => import("../pages/channels/ChannelsPage.js"), "ChannelsPage");
const AdminPage = lazyNamed(() => import("../pages/admin/AdminPage.js"), "AdminPage");
const QueuesPage = lazyNamed(() => import("../pages/queues/QueuesPage.js"), "QueuesPage");
const SettingsPage = lazyNamed(() => import("../pages/settings/SettingsPage.js"), "SettingsPage");
const AuditPage = lazyNamed(() => import("../pages/audit/AuditPage.js"), "AuditPage");
const DigestPage = lazyNamed(() => import("../pages/digest/DigestPage.js"), "DigestPage");
const NotificationsPage = lazyNamed(() => import("../pages/notifications/NotificationsPage.js"), "NotificationsPage");
const WorkflowsPage = lazyNamed(() => import("../pages/workflows/WorkflowsPage.js"), "WorkflowsPage");
const RecordingReviewPage = lazyNamed(() => import("../pages/recording-review/RecordingReviewPage.js"), "RecordingReviewPage");
const RecordingsInboxPage = lazyNamed(() => import("../pages/recordings/RecordingsInboxPage.js"), "RecordingsInboxPage");
const InvitationAcceptPage = lazyNamed(() => import("../pages/invitations/InvitationAcceptPage.js"), "InvitationAcceptPage");
const ResetPasswordPage = lazyNamed(() => import("../pages/auth/ResetPasswordPage.js"), "ResetPasswordPage");

// ---------------------------------------------------------------------------
// Wrappers de rota — OBRIGATORIAMENTE no escopo do módulo
// ---------------------------------------------------------------------------

/**
 * Estes dois wrappers moravam DENTRO de `AppRouter`, e isso era um defeito de verdade, não
 * questão de estilo: uma função declarada dentro de um componente é recriada a cada render, e o
 * React identifica componentes pela identidade do tipo. Tipo novo na mesma posição = subárvore
 * DESMONTADA e remontada do zero, com todo o estado local da página junto.
 *
 * O sintoma relatado foi a tela de Configuração da Plataforma voltando para a sub-aba "Branding"
 * ao reordenar um estágio, mas o alcance era a aplicação inteira: qualquer render do roteador —
 * e a renovação silenciosa do token provoca um, porque chama `setUser` — apagava aba aberta,
 * modal e formulário não salvo de QUALQUER tela. Coberto por `src/tests/router-remount.test.tsx`.
 *
 * `rbacCtx` chega por propriedade em vez de closure, que é o que permite a subida.
 */
function Protected({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <Shell>{children}</Shell>
    </RequireAuth>
  );
}

function ProtectedWithRole({
  route,
  rbacCtx,
  children,
}: {
  route: Parameters<typeof RequireRole>[0]["route"];
  rbacCtx: RbacContext;
  children: React.ReactNode;
}) {
  return (
    <RequireAuth>
      <Shell>
        <RequireRole route={route} rbacCtx={rbacCtx}>
          {children}
        </RequireRole>
      </Shell>
    </RequireAuth>
  );
}

// ---------------------------------------------------------------------------
// AppRouter — declaração das 15 rotas (Lista Canônica 4.1)
// ---------------------------------------------------------------------------
export function AppRouter() {
  const { user, sessionState } = useAuth();

  /** RBAC derivado do usuário autenticado — role extraído do claim JWT (ADR-0015).
   *  Fallback "sales_ae" (deny-by-default) quando o campo role não está presente
   *  (ex.: mocks de teste sem role). */
  const userRole: UserRole = user?.role ?? "sales_ae";
  const rbacCtx: RbacContext = user
    ? {
        roles: [userRole],
        isMaster: userRole === "platform_operator",
        canSwitchBU: userRole === "platform_operator" || userRole === "tenant_admin",
      }
    : { roles: [], isMaster: false, canSwitchBU: false };

  const isAuthenticated = !!user && sessionState === "authenticated";

  return (
    <Routes>
      {/* Autenticação — redireciona para dashboard se já autenticado */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            // Pós-login sempre vai para a visão geral (dashboard). `azim.route` é
            // persistido em localStorage (sobrevive entre sessões/abas) e levava o
            // usuário a uma rota antiga (ex.: /settings) ao reautenticar — surpresa
            // indesejada. O destino previsível é o dashboard.
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthPage />
          )
        }
      />

      {/*
        Aceite de convite — anônima por construção, e por isso declarada FORA de
        Protected/ProtectedWithRole e ANTES do catch-all.
        O convidado não tem sessão: sob o RequireAuth ele seria expulso para /login antes de a página montar, e sem rota própria cairia no catch-all rumo a /dashboard e de lá para /login — que é exatamente o sintoma observado quando o link do e-mail chegava ao host certo.
        Também não passa pelo RBAC nem pelo portão de disponibilidade de funcionalidade (`canSeeRoute`): não há papel a consultar antes de o usuário existir, e um ambiente sem AZIM_ENABLED_FEATURES derrubaria o onboarding inteiro.
        Lazy porque não é LCP: quem chega aqui vem de um clique no e-mail, não da navegação do produto.
      */}
      <Route
        path="/invitations/accept"
        element={
          <Suspense fallback={<PageLoader />}>
            <InvitationAcceptPage />
          </Suspense>
        }
      />

      {/*
        Redefinição de senha — anônima pelas MESMAS razões do aceite de convite acima, e declarada
        aqui pelo mesmo motivo: fora de Protected/ProtectedWithRole e ANTES do catch-all.
        O caminho `/auth/reset-password` não é escolha nossa — é o que o serviço authentication
        compõe no link do e-mail (`{AppBaseUrl}/auth/reset-password?token=…`). Enquanto esta rota
        não existiu, o link caía no catch-all, o portal mandava a pessoa para /dashboard, de lá para
        /login, e o token era descartado: o e-mail chegava e não servia para nada.
        Não colide com `/v1/auth/**` do gateway, que é prefixado por /v1 e nunca chega ao portal.
      */}
      <Route
        path="/auth/reset-password"
        element={
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />

      {/* Rota raiz — redireciona para dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* ----------------------------------------------------------------
          Rotas comerciais — visíveis a todos os autenticados (Lista Canônica 4.1)
          ---------------------------------------------------------------- */}

      <Route
        path="/dashboard"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />

      {/* Pipeline — lazy (tela pesada, DD-005) */}
      <Route
        path="/pipeline"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <PipelinePage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/opportunity/:id"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <OpportunityDetailPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/accounts"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <AccountsPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/account/:id"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <Account360Page />
            </Suspense>
          </Protected>
        }
      />

      {/* Atividades — lazy (tela com lista virtualizada, DD-005).
          Abas (Meu Dia | Lista | Calendário) navegáveis via `:view`
          (deep-link, F5-safe) — mesmo padrão de /reports/:section. */}
      <Route
        path="/activities"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <ActivitiesPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/activities/:view"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <ActivitiesPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/inbox"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <InboxPage />
            </Suspense>
          </Protected>
        }
      />

      {/*
        Atendimento — `ProtectedWithRole` e não `Protected` apesar de a rota ser
        livre a qualquer autenticado (ROUTE_ROLES.conversations é null): é por
        `canSeeRoute` que passa o portão de disponibilidade de funcionalidade,
        então usar o guard é o que impede o acesso por link direto enquanto a
        tela não está liberada. Sem ele, esconder o item do menu deixaria
        /conversations acessível a quem digitasse a URL.
      */}
      <Route
        path="/conversations"
        element={
          <ProtectedWithRole route="conversations" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <ConversationsPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Canais — restrito a tenant/master (Wave 2: messaging-gateway) */}
      <Route
        path="/channels"
        element={
          <ProtectedWithRole route="channels" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <ChannelsPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Parcerias — restrito a manager/tenant/master */}
      <Route
        path="/partners"
        element={
          <ProtectedWithRole route="partners" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <PartnersPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      <Route
        path="/partner/:id"
        element={
          <ProtectedWithRole route="partners" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <PartnerDetailPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Relatórios — lazy + restrito a manager/tenant/master */}
      <Route
        path="/reports"
        element={
          <ProtectedWithRole route="reports" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <ReportsPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Subpáginas de Relatórios (funnel, funnel-by-account, forecast, ranking) */}
      <Route
        path="/reports/:section"
        element={
          <ProtectedWithRole route="reports" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <ReportsPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* ----------------------------------------------------------------
          Rotas administrativas (Lista Canônica 4.1)
          ---------------------------------------------------------------- */}

      {/* Configurações — abas (Perfil | Segurança | Preferências | Calendários)
          navegáveis via `:section` (deep-link, F5-safe) — mesmo padrão de
          /reports/:section e /activities/:view. */}
      <Route
        path="/settings"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/settings/:section"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          </Protected>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedWithRole route="admin" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <AdminPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Filas de atendimento — Wave 3 */}
      <Route
        path="/queues"
        element={
          <ProtectedWithRole route="queues" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <QueuesPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Plataforma — lazy + restrito a tenant/master */}
      <Route
        path="/platform"
        element={
          <ProtectedWithRole route="platform" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <PlatformPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Importação — lazy + restrito a tenant/master */}
      <Route
        path="/import"
        element={
          <ProtectedWithRole route="import" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <ImportPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      <Route
        path="/audit"
        element={
          <ProtectedWithRole route="audit" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <AuditPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      <Route
        path="/digest"
        element={
          <ProtectedWithRole route="digest" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <DigestPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      <Route
        path="/notifications"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <NotificationsPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Automações — Onda 6 (wave 6: workflow-automation) */}
      <Route
        path="/workflows"
        element={
          <ProtectedWithRole route="workflows" rbacCtx={rbacCtx}>
            <Suspense fallback={<PageLoader />}>
              <WorkflowsPage />
            </Suspense>
          </ProtectedWithRole>
        }
      />

      {/* Inbox de gravações aguardando classificação — acessível a qualquer autenticado */}
      <Route
        path="/recordings"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <RecordingsInboxPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Revisão de gravação — descoberta via inbox; acessível a qualquer autenticado */}
      <Route
        path="/recording/:id/review"
        element={
          <Protected>
            <Suspense fallback={<PageLoader />}>
              <RecordingReviewPage />
            </Suspense>
          </Protected>
        }
      />

      {/* Unauthorized */}
      <Route
        path="/unauthorized"
        element={
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <h1>Acesso negado</h1>
            <p>Você não tem permissão para acessar esta página.</p>
          </div>
        }
      />

      {/* Fallback — redireciona para dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
